import { create } from 'zustand';
import type { UploadedImage } from '@/components/features/ImageUploadZone/ImageUploadZone.types';
import type { Product } from '@/types/Product.types';

export enum CurationPhase {
  UPLOAD = 'upload',
  SELECT = 'select',
  REVIEW = 'review',
  FINALIZE = 'finalize',
}

export interface CurationFilters {
  minPrice: string;
  maxPrice: string;
  brands: string[];
  marketplaces: string[];
}

export interface CurationPagination {
  page: number;
  hasMore: boolean;
  totalPages: number;
  totalItems: number;
}

export interface CurationMetadata {
  title: string;
  description: string;
  curator: string;
  widgetImage: string;
  curationImage: string;
  imageTitle: string;
  isHomepageCuration: boolean;
}

const initialFilters: CurationFilters = {
  minPrice: '',
  maxPrice: '',
  brands: [],
  marketplaces: [],
};

const initialMetadata: CurationMetadata = {
  title: '',
  description: '',
  curator: '',
  widgetImage: '',
  curationImage: '',
  imageTitle: '',
  isHomepageCuration: false,
};

const MAX_PRODUCTS = 500;

interface CreateCurationState {
  // ── Phase ──────────────────────────────────────
  currentPhase: CurationPhase;
  setPhase: (phase: CurationPhase) => void;

  // ── Upload step ────────────────────────────────
  uploadedImages: UploadedImage[];
  addImages: (files: File[]) => void;
  removeImage: (index: number) => void;
  updateImageUploadProgress: (index: number, progress: number) => void;
  updateImageUrl: (index: number, url: string) => void;
  setImageError: (index: number, error: string) => void;
  sale: boolean;
  setSale: (v: boolean) => void;

  // ── Select step – filters ──────────────────────
  filters: CurationFilters;
  setFilters: (patch: Partial<CurationFilters>) => void;
  resetFilters: () => void;

  // ── Select step – generated feed (paginated) ──
  generatedFeed: Product[];
  setGeneratedFeed: (products: Product[]) => void;
  appendToFeed: (products: Product[]) => void;
  clearFeed: () => void;

  // ── Select step – pagination ───────────────────
  pagination: CurationPagination | null;
  setPagination: (p: CurationPagination | null) => void;

  // ── Select step – selected products ───────────
  selectedProducts: Product[];
  toggleProductSelection: (product: Product) => void;
  isProductSelected: (productId: number) => boolean;
  clearSelectedProducts: () => void;
  setSelectedProducts: (products: Product[]) => void;

  // ── Review step – working set ──────────────────
  // Initialised from dedup(existingProducts + selectedProducts) on entering REVIEW.
  // All REVIEW edits (remove / replace / reorder) operate here.
  reviewProducts: Product[];
  setReviewProducts: (products: Product[]) => void;
  removeReviewProduct: (productId: number) => void;
  replaceReviewProduct: (oldId: number, newProduct: Product) => void;
  reorderReviewProducts: (startIndex: number, endIndex: number) => void;

  // ── Edit mode – existing products ─────────────
  existingProducts: Product[];
  setExistingProducts: (products: Product[]) => void;

  // ── Finalize step – metadata ───────────────────
  metadata: CurationMetadata;
  updateMetadata: (field: keyof CurationMetadata, value: string) => void;

  // ── Loading / error ────────────────────────────
  isGeneratingFeed: boolean;
  setIsGeneratingFeed: (v: boolean) => void;
  isSubmitting: boolean;
  setIsSubmitting: (v: boolean) => void;
  error: string | null;
  setError: (e: string | null) => void;

  // ── Helpers ────────────────────────────────────
  /** Merge existingProducts + selectedProducts (deduped) into reviewProducts. */
  initReviewProducts: () => void;
  reset: () => void;
}

const useCreateCurationStore = create<CreateCurationState>((set, get) => ({
  // ── Phase ──────────────────────────────────────
  currentPhase: CurationPhase.UPLOAD,
  setPhase: (phase) => set({ currentPhase: phase }),

  // ── Upload step ────────────────────────────────
  uploadedImages: [],
  sale: false,
  setSale: (v) => set({ sale: v }),

  addImages: (files) => {
    const newImages: UploadedImage[] = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isUploading: false,
      uploadProgress: 0,
    }));
    set((state) => ({
      uploadedImages: [...state.uploadedImages, ...newImages],
    }));
  },

  removeImage: (index) => {
    const image = get().uploadedImages[index];
    if (image?.preview) URL.revokeObjectURL(image.preview);
    set((state) => ({
      uploadedImages: state.uploadedImages.filter((_, i) => i !== index),
    }));
  },

  updateImageUploadProgress: (index, progress) =>
    set((state) => ({
      uploadedImages: state.uploadedImages.map((img, i) =>
        i === index
          ? { ...img, isUploading: true, uploadProgress: progress }
          : img
      ),
    })),

  updateImageUrl: (index, url) =>
    set((state) => ({
      uploadedImages: state.uploadedImages.map((img, i) =>
        i === index
          ? { ...img, url, isUploading: false, uploadProgress: 100 }
          : img
      ),
    })),

  setImageError: (index, error) =>
    set((state) => ({
      uploadedImages: state.uploadedImages.map((img, i) =>
        i === index ? { ...img, error, isUploading: false } : img
      ),
    })),

  // ── Filters ────────────────────────────────────
  filters: initialFilters,

  setFilters: (patch) =>
    set((state) => ({ filters: { ...state.filters, ...patch } })),

  resetFilters: () => set({ filters: initialFilters }),

  // ── Generated feed ─────────────────────────────
  generatedFeed: [],

  setGeneratedFeed: (products) =>
    set({ generatedFeed: products.slice(0, MAX_PRODUCTS) }),

  appendToFeed: (products) =>
    set((state) => {
      const existingIds = new Set(state.generatedFeed.map((p) => p.id));
      const fresh = products.filter((p) => !existingIds.has(p.id));
      const combined = [...state.generatedFeed, ...fresh];
      return { generatedFeed: combined.slice(0, MAX_PRODUCTS) };
    }),

  clearFeed: () => set({ generatedFeed: [], pagination: null }),

  // ── Pagination ─────────────────────────────────
  pagination: null,
  setPagination: (p) => set({ pagination: p }),

  // ── Selected products ──────────────────────────
  selectedProducts: [],

  toggleProductSelection: (product) =>
    set((state) => {
      const already = state.selectedProducts.find((p) => p.id === product.id);
      return {
        selectedProducts: already
          ? state.selectedProducts.filter((p) => p.id !== product.id)
          : [...state.selectedProducts, product],
      };
    }),

  isProductSelected: (productId) =>
    get().selectedProducts.some((p) => p.id === productId),

  clearSelectedProducts: () => set({ selectedProducts: [] }),

  setSelectedProducts: (products) => set({ selectedProducts: products }),

  // ── Review products ────────────────────────────
  reviewProducts: [],
  setReviewProducts: (products) => set({ reviewProducts: products }),

  removeReviewProduct: (productId) =>
    set((state) => ({
      reviewProducts: state.reviewProducts.filter((p) => p.id !== productId),
    })),

  replaceReviewProduct: (oldId, newProduct) =>
    set((state) => ({
      reviewProducts: state.reviewProducts.map((p) =>
        p.id === oldId ? newProduct : p
      ),
    })),

  reorderReviewProducts: (startIndex, endIndex) =>
    set((state) => {
      const products = [...state.reviewProducts];
      if (
        startIndex === endIndex ||
        startIndex < 0 ||
        endIndex < 0 ||
        startIndex >= products.length ||
        endIndex >= products.length
      )
        return state;
      const [removed] = products.splice(startIndex, 1);
      products.splice(endIndex, 0, removed);
      return { reviewProducts: products };
    }),

  // ── Existing products (edit mode) ──────────────
  existingProducts: [],
  setExistingProducts: (products) => set({ existingProducts: products }),

  // ── Metadata ───────────────────────────────────
  metadata: initialMetadata,

  updateMetadata: (field, value) =>
    set((state) => ({
      metadata: {
        ...state.metadata,
        [field]: field === 'isHomepageCuration' ? value === 'true' : value,
      },
    })),

  // ── Loading / error ────────────────────────────
  isGeneratingFeed: false,
  setIsGeneratingFeed: (v) => set({ isGeneratingFeed: v }),
  isSubmitting: false,
  setIsSubmitting: (v) => set({ isSubmitting: v }),
  error: null,
  setError: (e) => set({ error: e }),

  // ── Helpers ────────────────────────────────────
  initReviewProducts: () => {
    const { existingProducts, selectedProducts } = get();
    const seen = new Set<number>();
    const merged: Product[] = [];
    [...existingProducts, ...selectedProducts].forEach((p) => {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        merged.push(p);
      }
    });
    set({ reviewProducts: merged });
  },

  reset: () => {
    get().uploadedImages.forEach((img) => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    set({
      currentPhase: CurationPhase.UPLOAD,
      uploadedImages: [],
      sale: false,
      filters: initialFilters,
      generatedFeed: [],
      pagination: null,
      selectedProducts: [],
      reviewProducts: [],
      existingProducts: [],
      metadata: initialMetadata,
      isGeneratingFeed: false,
      isSubmitting: false,
      error: null,
    });
  },
}));

export default useCreateCurationStore;
